/* eslint-disable camelcase */
import classNames from 'classnames';
import { get } from 'lodash';
import { isNil } from 'lodash-es';
import moment from 'moment';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Alert, Container, Nav } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Route, Switch, useHistory, useLocation, useRouteMatch } from "react-router-dom";
import {
  resetOfferStore,
  setGlobalAlert,
  setOffer,
  setOfferStore,
  setTabPaddingTop,
  setUserSignedUp,
  showSplashScreen
} from '../../../actions/offer.actions';
import FlashSuccess from '../../../shared/components/FlashSuccess/FlashSuccess';
import useBreakpoint from '../../../shared/hooks/useBreakpoint';
import useIsMounted from '../../../shared/hooks/useIsMounted';
import axios from '../../../utils/axiosConfig';
import { OFFER_STAGE, OFFER_STATUSES } from '../../../utils/constants';
import { ERROR_REASONS, ERROR_REFERENCE } from '../../../utils/errors';
import MoodboardCampaign from '../moodbooard/MoodboardCampaign';
import Brief from './Brief/Brief';
import General from './General/General';
import './Offer.scss';
import Questions from './Questions/Questions';
import Brand from './components/Brand';
import PromptOffer from './components/PromptOffer';
import Loader from '../../../shared/components/Loader/Loader';
import TOSAcceptance from "../../../shared/components/TOSAcceptance";
import { signOut } from '../../../actions/auth.actions';

const Offer = ({
  simulatedUserId,
  language
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const match = useRouteMatch();
  const isMounted = useIsMounted();
  const isInitialMount = useRef(true);
  const dispatch = useDispatch();

  /** @type {import('../../../reducers/offer.reducer').OfferStore} */
  const context = useSelector(store => get(store, 'offer'));
  const isLogged = useSelector(store => get(store, 'auth.user'));
  const offer = get(context, 'offer');
  const breakpoint = useBreakpoint();
  const { token: accessToken } = queryString.parse(window.location.search);
    

  const [state, setState] = useState({
    activeKey: match.params.activeTab || '/',
    offerUndeclined: true,
    simulatedUserId: null,
    loading: true
  });

  const handleLogout = useCallback(() => {
    axios.delete('/api/v1/influencers/users/sign_out')
      .then(({ data }) => {
        signOut(dispatch);
        history.push('/signin');
        window.location.reload(true);
      }).catch(() => {
        dispatch(addFlashMessage({
          text: t('influencers.common.message_error'),
          type: 'error'
        }));
      });
  }, [history, signOut]);

  const retrieveOffer = async (offerId, extraState) => {
    dispatch(setOfferStore({
      offerId,
    }));

    const queryParams = queryString.parse(location.search);
    const { youtube_oauth_callback, facebook_oauth_callback } = queryParams;

    const callbackAuth = (!isNil(youtube_oauth_callback) && ["true", "false"].includes(youtube_oauth_callback));

    let newState = {
      ...state,
      ...extraState,
      simulatedUserId
    };

    let apiPath = `/api/v1/influencers/offer_data?offer_id=${offerId}&language=${language}`;
    if (accessToken) {
      apiPath += `&token=${accessToken}`;
    }

    return axios.get(apiPath)
      .then(({ data }) => {
        if (!isMounted) return;

        dispatch(setOfferStore({
          path: `/offer/${offerId}`,
          offerId,
          callbackAuth
        }));

        const newOffer = data;
        // Setting all vars related to the offer
        const userSignedUp = !!get(newOffer, 'user_offer.participation_form_completed_at', false);
        let tabPaddingTop = 28;
        if (breakpoint === 'xs') {
          tabPaddingTop = 120;
        }
        if (userSignedUp) {
          tabPaddingTop = null;
        }

        // Every data coming from the offer should be set at this point
        newState = {
          ...newState,
          offerUndeclined: get(newOffer, 'user_offer.status', null) !== OFFER_STATUSES.DECLINED,
          loading: false,
        };

        setState(newState);
        dispatch(setOffer(newOffer));
        // Determines if user still joining the offer or not
        dispatch(setUserSignedUp(!!get(newOffer, 'user_offer.participation_form_completed_at', false)));
        dispatch(setTabPaddingTop(tabPaddingTop));
      }).catch((error) => {
        if (!isMounted) return;

        if (!isLogged && [404, 401].includes(get(error.response, 'status'))) {
          return history.push({
            pathname: '/signin',
            search: `?from=${location.pathname}`
          });
        }

        if ([401].includes(get(error.response, 'status'))) {

          //double-check session

          axios.get("/api/v1/influencers/influencers/get_user.json").
          then().
          catch((e) => {
            console.log(e);
            if ([401].includes(get(e.response, 'status')))
              handleLogout();
          });
        }
        

        if (isLogged && (get(error.response, 'status') == 404)) {
          return history.push({
            pathname: '/'
          });
        }

        setState(newState);

        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

        const cause = {
          code: ERROR_REFERENCE.OFFER,
          id: offerId
        };
        let errorTmp = {
          type: ERROR_REASONS.NOT_FOUND,
          cause
        };

        if (!isSafari) {
          errorTmp = new Error(ERROR_REASONS.NOT_FOUND, {
            cause
          });
        }

        history.push('/404', { error: errorTmp });
        dispatch(setOffer(null));
      });
  };

  useEffect(() => {
    const contentApproval = get(offer, 'timelines.content_approval');
    if (
      !state.loading &&
      OFFER_STAGE.CONTENT_APPROVAL.includes(get(offer, 'user_offer.status', '')) &&
      contentApproval
    ) {
      const limitDate = moment(new Date(contentApproval)).add(1, 'days');
      const daysLeft = limitDate.diff(moment(), 'days');

      if (moment().isAfter(limitDate)) {
        return dispatch(setGlobalAlert({
          content: <React.Fragment>
            <i className="icon fal fa-exclamation-circle"></i>
            {t('influencers.pages.campaign.deadline_danger')}
          </React.Fragment>,
          variant: 'danger'
        }));
      }
      else if (daysLeft == 0) {
        return dispatch(setGlobalAlert({
          content: <React.Fragment>
            <i className="icon fal fa-exclamation-circle"></i>
            {t('influencers.pages.campaign.deadline_today')}
          </React.Fragment>,
          variant: 'danger',
        }));
      }
      else if (daysLeft <= 3) {
        return dispatch(setGlobalAlert({
          content: <React.Fragment>
            <i className="icon fal fa-exclamation-triangle"></i>
            {t('influencers.pages.campaign.deadline_warning', {
              days: daysLeft
            })}
          </React.Fragment>,
          variant: 'danger'
        }));
      }
    }
  }, [context.offerId]);

  useEffect(() => {
    if (get(offer, 'splash_pages.hired')) {
      dispatch(showSplashScreen(
        true,
        `🤗✨🏆\n${t('influencers.pages.campaign.you_re_hired')}`
      ));
    }
  }, [context.offerId]);

  useEffect(() => {
    let { offerId } = match.params;
    // If the current offer id is different from the previous ask for new data
    if (offerId && (context.offerId !== offerId || state.simulatedUserId !== simulatedUserId)) {
      const rx = /^[^\d]*(\d+)/;
      offerId = rx.exec(offerId)[1];
      retrieveOffer(offerId);
    }

    // Checks if the active tab has change due an external event
    const { activeTab } = match.params || '/';
    const { activeKey } = state;
    if (activeTab !== activeKey) {
      setState({
        ...state,
        activeKey: activeTab
      });
    }
  }, [match]);

  useEffect(() => () => {
    dispatch(resetOfferStore());
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else if (context.offerId) {
      retrieveOffer(context.offerId);
    }
  }, [language]);

  useEffect(() => {
    if (context.updateOffer) retrieveOffer(context.offerId);
  }, [context.updateOffer]);

  const { path } = context;

  if (state.loading) {
    return <Container className="pt-5"><Loader loading /></Container>;
  }

  const { activeKey } = state;
  const { user_offer: userOffer } = offer;

  const tipaltiPaymentSetupNeeded = offer.tipalti_payment_processing && !user.is_payable_by_tipalti && user.admin;

  const showPromptOffer = (!context.userSignedUp && state.offerUndeclined) || tipaltiPaymentSetupNeeded || context.callbackAuth;
  const displayAllTabs = !offer.is_seeding || (offer.is_seeding && context.userSignedUp);

  let tokenPath = '';
  if (accessToken) {
    tokenPath = `?token=${accessToken}`;
  }

  return (
    <div className="home-page">
      <TOSAcceptance />
      <Nav
        className="navbar-secondary sticky-top"
        bg="light"
        variant="tabs"
        defaultActiveKey={activeKey || '/'}
        activeKey={activeKey || '/'}>
        <Nav.Link as={Link} eventKey="/" to={`${path}${tokenPath}`}>
          {t('influencers.pages.campaign.general')}
        </Nav.Link>
        {displayAllTabs && (
          <React.Fragment>
            <Nav.Link as={Link} eventKey="brief" to={`${path}/brief${tokenPath}`}>
              {t('influencers.pages.campaign.brief.tab')}
            </Nav.Link>

            {offer.moodboard_images && (
              <Nav.Link as={Link} eventKey="moodboard" to={`${path}/moodboard${tokenPath}`}>
                {t('influencers.pages.campaign.moodboard.title')}
              </Nav.Link>
            )}
            <Nav.Link as={Link} eventKey="questions" to={`${path}/questions${tokenPath}`}>
              {t('influencers.pages.campaign.questions.tab')}
            </Nav.Link>
            <Nav.Link as={Link} eventKey="brand" to={`${path}/brand${tokenPath}`}>
              {t('influencers.pages.campaign.brand.title')}
            </Nav.Link>
          </React.Fragment>
        )}
      </Nav>

      {context.alertSettings &&
        <Container className="pt-5">
          <Alert className="mt-4"
            dismissible
            onClose={() => dispatch(setGlobalAlert({ content: null }))}
            variant={context.alertSettings.variant}>
            {context.alertSettings.content}
          </Alert>
        </Container>
      }

      {
        showPromptOffer &&
        <PromptOffer
          canJoin={offer.actionable}
          maxReached={offer.unactionable_reason}
          paused={offer.paused} />
      }

      <div className={classNames("offer-tab", {
        'has-prompt': !context.userSignedUp
      })}>
        <Switch>
          <Route exact path={path}>
            <div className={classNames("offer-tab")}
              style={{
                paddingTop: state.tabPaddingTop
              }}>
              <General offer={offer} />
            </div>
          </Route>

          {displayAllTabs && (
            <React.Fragment>
              <Route path={`${path}/brief`}>
                <Brief
                  creativeBrief={offer.creative_brief}
                  offer={offer}
                />
              </Route>

              {offer.moodboard_images && (
                <Route path={`${path}/moodboard`}>
                  <MoodboardCampaign offer={offer} />
                </Route>
              )}

              <Route path={`${path}/questions`}>
                <Questions offer={offer} />
              </Route>
              <Route path={`${path}/brand`}>
                <Brand offer={offer} />
              </Route>
            </React.Fragment>
          )}
        </Switch >

        {
          context.splashSettings && <FlashSuccess timeout={context.splashSettings.timeout}
            isOpen={context.splashSettings.show}
            onClose={() => dispatch(showSplashScreen(false))}>
            <h3 className='text-center'>
              {context.splashSettings.text}
            </h3>
          </FlashSuccess>
        }
      </div >
    </div >
  );
};

Offer.propTypes = {
  simulatedUserId: PropTypes.string,
  language: PropTypes.string.isRequired
};

export default Offer;
